import { FormulaQuery } from 'dashboard-services/api/formula';
import { CommonService } from 'dashboard-services/services';

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export default (() => {
  // Any formula write can change other formulas' dependency trees (dependents), so every formula query is refreshed.
  const useInvalidateFormulas = ({ onSuccess }) => {
    const queryClient = useQueryClient()
    return (...args) => {
      queryClient.invalidateQueries({ queryKey: FormulaQuery.KEYS.ALL })
      return onSuccess?.(...args)
    }
  }

  // `sourceGroup`/`sourceName` filter on the originating Insight; `query` takes extra Datalake filters in
  // `field=value&field=value` form (e.g. `name.keyword=*rev*`).
  const useSearchFormulas = ({ getQueryBuilder, groupName, name, sourceGroup, sourceName, latest, tags, query, size = CommonService.getRequestSize(), enabled = true, onSelect, shouldRefetch = true, shouldBeLimited, ...rest }) =>
    useInfiniteQuery({
      ...FormulaQuery.QUERIES.search(
        getQueryBuilder,
        {
          groupName,
          name,
          sourceGroup,
          sourceName,
          latest,
          tags,
          query,
          size
        }
      ),
      refetchInterval: () => !shouldRefetch || shouldBeLimited?.() ? undefined : 10_000,
      enabled,
      select: onSelect,
      ...rest
    })

  const useSearchFormulasMutation = ({ getQueryBuilder, groupName, name, sourceGroup, sourceName, latest, tags, query, size = CommonService.getRequestSize(), ...rest }) =>
    useMutation({
      ...FormulaQuery.QUERIES.searchMutation(
        getQueryBuilder,
        {
          groupName,
          name,
          sourceGroup,
          sourceName,
          latest,
          tags,
          query,
          size
        }
      ),
      ...rest
    })

  // `includeTemplate` adds `resolvedTemplate` (the latest referenced template) to a template-based formula.
  const useGetFormulaById = ({ getQueryBuilder, fileId, includeTemplate, enabled = true, ...rest }) =>
    useQuery({
      ...FormulaQuery.QUERIES.getById(
        getQueryBuilder,
        {
          fileId,
          includeTemplate
        }
      ),
      enabled: enabled && !!fileId,
      ...rest
    })

  const useGetFormulaByIdMutation = ({ getQueryBuilder, fileId, includeTemplate, ...rest }) =>
    useMutation({
      ...FormulaQuery.QUERIES.getByIdMutation(
        getQueryBuilder,
        {
          fileId,
          includeTemplate
        }
      ),
      ...rest
    })

  const useGetFormula = ({ getQueryBuilder, groupName, name, includeTemplate, enabled = true, ...rest }) =>
    useQuery({
      ...FormulaQuery.QUERIES.getByName(
        getQueryBuilder,
        {
          groupName,
          name,
          includeTemplate
        }
      ),
      enabled: enabled && !!groupName && !!name,
      ...rest
    })

  const useGetFormulaVersion = ({ getQueryBuilder, groupName, name, version, includeTemplate, enabled = true, ...rest }) =>
    useQuery({
      ...FormulaQuery.QUERIES.getVersion(
        getQueryBuilder,
        {
          groupName,
          name,
          version,
          includeTemplate
        }
      ),
      enabled: enabled && !!groupName && !!name && version !== undefined && version !== null,
      ...rest
    })

  const useGetFormulaDependencyTree = ({ getQueryBuilder, groupName, name, direction, enabled = true, ...rest }) =>
    useQuery({
      ...FormulaQuery.QUERIES.getDependencyTree(
        getQueryBuilder,
        {
          groupName,
          name,
          direction
        }
      ),
      enabled: enabled && !!groupName && !!name,
      ...rest
    })

  const useIsSymbolArrivalTriggerAvailable = ({ getQueryBuilder }) =>
    useQuery({
      ...FormulaQuery.QUERIES.isSymbolArrivalWorkerAvailable(getQueryBuilder),
      staleTime: 'static',
      gcTime: Infinity,
      retry: false,
      retryOnMount: false
    })

  const useCreateFormula = ({ getQueryBuilder, name, groupName, tags, fields, content, onSuccess, ...rest }) =>
    useMutation({
      ...FormulaQuery.QUERIES.create(
        getQueryBuilder,
        {
          name,
          groupName,
          tags,
          fields,
          content
        }
      ),
      onSuccess: useInvalidateFormulas({ onSuccess }),
      ...rest
    })

  // Creates a new revision of the group/name identity. The revision stores exactly the metadata sent (nothing is merged
  // from the previous one): pass `tags` (or keep `tags` in `fields`) to carry them over; `[]` clears them.
  const useUpdateFormula = ({ getQueryBuilder, name, groupName, tags, fields, content, onSuccess, ...rest }) =>
    useMutation({
      ...FormulaQuery.QUERIES.update(
        getQueryBuilder,
        {
          name,
          groupName,
          tags,
          fields,
          content
        }
      ),
      onSuccess: useInvalidateFormulas({ onSuccess }),
      ...rest
    })

  const useDeleteFormulaById = ({ getQueryBuilder, fileId, onSuccess, ...rest }) =>
    useMutation({
      ...FormulaQuery.QUERIES.deleteById(
        getQueryBuilder,
        {
          fileId
        }
      ),
      onSuccess: useInvalidateFormulas({ onSuccess }),
      ...rest
    })

  const useDeleteFormulaVersion = ({ getQueryBuilder, groupName, name, version, onSuccess, ...rest }) =>
    useMutation({
      ...FormulaQuery.QUERIES.deleteVersion(
        getQueryBuilder,
        {
          groupName,
          name,
          version
        }
      ),
      onSuccess: useInvalidateFormulas({ onSuccess }),
      ...rest
    })

  // Deletes all revisions of the formula.
  const useDeleteFormula = ({ getQueryBuilder, groupName, name, onSuccess, ...rest }) =>
    useMutation({
      ...FormulaQuery.QUERIES.delete(
        getQueryBuilder,
        {
          groupName,
          name
        }
      ),
      onSuccess: useInvalidateFormulas({ onSuccess }),
      ...rest
    })

  return {
    useSearchFormulas,
    useSearchFormulasMutation,
    useGetFormulaById,
    useGetFormulaByIdMutation,
    useGetFormula,
    useGetFormulaVersion,
    useGetFormulaDependencyTree,
    useIsSymbolArrivalTriggerAvailable,
    useCreateFormula,
    useUpdateFormula,
    useDeleteFormulaById,
    useDeleteFormulaVersion,
    useDeleteFormula
  }
})()
